import type { ReactNode } from "react";
import { useTenantImages } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import { LobbyTripCard } from "@hopper-ai/hts-assist-components";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { AirlineIcon } from "@tenant/ui";
import { Car, Hotel, Plane, Ticket, House, Package } from "lucide-react";
import type { HtsAssistLobbyTrip } from "./htsAssistLobbyTrips";
import { usePackageReservationCard } from "@/components/packages/PackageReservationCard/usePackageReservationCard";
import {
  useTripsReservationStatusPill,
  type TripsReservationStatusPillProps,
} from "@/components/shared/TripsReservationStatusPill";
import { useHomesReservationCard } from "@/modules/homes-postbooking/components/HomesReservationCard/useHomesReservationCard";
import { useActivityReservationCard } from "@/overrides/activities/postBooking/components/ActivityReservationCard/useActivityReservationCard";
import { useCarReservationCard } from "@/overrides/cars/components/CarReservationCard/useCarReservationCard";
import { useFlightItineraryCard } from "@/overrides/flights/components/FlightItineraryCard/useFlightItineraryCard";
import { useHotelReservationCard } from "@/overrides/hotels/components/HotelReservationCard/useHotelReservationCard";

interface HtsAssistLobbyTripCardProps {
  trip: HtsAssistLobbyTrip;
  onSelect: (trip: HtsAssistLobbyTrip) => void;
}

const ConfirmationLabel = ({ code }: { code: string }) => (
  <Trans
    i18nKey="chat.htsAssistLobbyItineraryLabelAndCode"
    values={{ code }}
    components={{ b: <strong /> }}
  />
);

const HtsAssistLobbyStatusBadge = (props: TripsReservationStatusPillProps) => {
  const status = useTripsReservationStatusPill(props);

  if (!status) {
    return null;
  }

  return (
    <span className="hts-assist-lobby__status-badge" data-tone={status.tone}>
      {status.label}
    </span>
  );
};

const SelectableCard = ({
  children,
  label,
  onSelect,
}: {
  children: ReactNode;
  label: string;
  onSelect: () => void;
}) => (
  <button
    aria-label={label}
    className="hts-assist-lobby__trip-button"
    onClick={onSelect}
    type="button"
  >
    {children}
  </button>
);

const FlightLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Flight }>;
}) => {
  const { t } = useI18nContext();
  const { text, context } = useFlightItineraryCard({
    itinerary: trip.reservation,
  });
  const backupIcon = context.airlineCode ? (
    <AirlineIcon airlineCode={context.airlineCode} size="large" />
  ) : (
    <Plane aria-hidden="true" />
  );

  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image=""
        backupIcon={backupIcon}
        title={text.title}
        description={text.description}
        dateLabel={text.dates}
        confirmationLabel={
          <ConfirmationLabel code={context.confirmationCode} />
        }
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.FLIGHTS}
            status={context.flightStatus}
            scheduleChangeSeverity={context.scheduleChangeSeverity}
          />
        }
      />
    </SelectableCard>
  );
};

const HotelLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Hotel }>;
}) => {
  const { t } = useI18nContext();
  const images = useTenantImages();
  const { text } = useHotelReservationCard({
    reservation: trip.reservation,
    selectItinerary: () => undefined,
  });

  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image={images.hotelPlaceholder}
        imageAlt={text.title}
        imageClassName="hts-assist-lobby__trip-image--cover"
        backupIcon={<Hotel aria-hidden="true" />}
        title={text.title}
        description={text.location}
        dateLabel={text.dates}
        confirmationLabel={
          <ConfirmationLabel code={trip.reservation.reservationId} />
        }
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.LODGING}
            status={trip.reservation.state.ReservationState}
          />
        }
      />
    </SelectableCard>
  );
};

const CarLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Ground }>;
}) => {
  const { t } = useI18nContext();
  const { text } = useCarReservationCard(trip.reservation.reservation);

  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image={trip.reservation.reservation.vehicle?.images?.original ?? ""}
        imageAlt={text.title}
        imageClassName="hts-assist-lobby__trip-image--cover"
        backupIcon={<Car aria-hidden="true" />}
        title={text.title}
        description={text.location}
        dateLabel={`${text.pickupDate} - ${text.dropOffDate}`}
        confirmationLabel={<ConfirmationLabel code={text.confirmation} />}
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.CARS}
            status={trip.reservation.reservation.state.ReservationState}
          />
        }
      />
    </SelectableCard>
  );
};

const ActivityLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Activities }>;
}) => {
  const { t } = useI18nContext();
  const { text, context } = useActivityReservationCard(trip.reservation);

  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image={context.image ?? ""}
        imageAlt={text.title}
        imageClassName="hts-assist-lobby__trip-image--cover"
        backupIcon={<Ticket aria-hidden="true" />}
        title={text.title}
        description={text.city}
        dateLabel={text.date}
        confirmationLabel={<ConfirmationLabel code={text.confirmationCode} />}
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.ACTIVITIES}
            status={context.activityStatus}
            isPastTrip={context.isPastTrip}
          />
        }
      />
    </SelectableCard>
  );
};

const PackageLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Package }>;
}) => {
  const { t } = useI18nContext();
  const images = useTenantImages();
  const { text } = usePackageReservationCard({
    reservation: trip.reservation,
    selectItinerary: () => onSelect(trip),
  });
  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image={images.packagePlaceholder}
        imageAlt={text.title}
        imageClassName="hts-assist-lobby__trip-image--cover"
        backupIcon={<Package aria-hidden="true" />}
        title={text.title}
        description={text.description}
        dateLabel={text.dates}
        confirmationLabel={<ConfirmationLabel code={text.confirmation} />}
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.PACKAGE}
            status={trip.reservation.state}
          />
        }
      />
    </SelectableCard>
  );
};

const HomesLobbyTripCard = ({
  trip,
  onSelect,
}: HtsAssistLobbyTripCardProps & {
  trip: Extract<HtsAssistLobbyTrip, { productType: typeof Product.Homes }>;
}) => {
  const { t } = useI18nContext();
  const images = useTenantImages();
  const { text } = useHomesReservationCard(trip.reservation);
  return (
    <SelectableCard
      label={t("chat.htsAssistLobbyOpenTrip", { title: text.title })}
      onSelect={() => onSelect(trip)}
    >
      <LobbyTripCard
        className="hts-assist-lobby__trip-card"
        image={trip.reservation.listing.coverImage || images.hotelPlaceholder}
        imageAlt={text.title}
        imageClassName="hts-assist-lobby__trip-image--cover"
        backupIcon={<House aria-hidden="true" />}
        title={text.title}
        description={text.location}
        dateLabel={text.dates}
        confirmationLabel={
          <ConfirmationLabel code={trip.reservation.id.value} />
        }
        statusBadge={
          <HtsAssistLobbyStatusBadge
            variant={ProductType.HOMES}
            status={trip.reservation.status.ReservationStatus}
          />
        }
      />
    </SelectableCard>
  );
};

export const HtsAssistLobbyTripCard = (props: HtsAssistLobbyTripCardProps) => {
  switch (props.trip.productType) {
    case Product.Package:
      return <PackageLobbyTripCard {...props} trip={props.trip} />;
    case Product.Homes:
      return <HomesLobbyTripCard {...props} trip={props.trip} />;
    case Product.Activities:
      return <ActivityLobbyTripCard {...props} trip={props.trip} />;
    case Product.Flight:
      return <FlightLobbyTripCard {...props} trip={props.trip} />;
    case Product.Hotel:
      return <HotelLobbyTripCard {...props} trip={props.trip} />;
    case Product.Ground:
      return <CarLobbyTripCard {...props} trip={props.trip} />;
  }
};
